import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { imageGenerationErrorMessage } from './imageGenerationJob'

export const MAX_AI_REFERENCE_IMAGES = 4
export const AI_REFERENCE_ACCEPT = 'image/png,image/jpeg,image/webp'

const AI_REFERENCE_CONTENT_TYPES = new Set(AI_REFERENCE_ACCEPT.split(','))
const AI_REFERENCE_CONTENT_TYPE_BY_EXTENSION = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp' }
const MAX_AI_REFERENCE_BYTES = 25 * 1024 * 1024

function referenceContentType(file) {
  const contentType = String(file?.type || '').toLocaleLowerCase()
  if (contentType) return AI_REFERENCE_CONTENT_TYPES.has(contentType) ? contentType : ''
  const extension = String(file?.name || '').split('.').pop()?.toLocaleLowerCase()
  return AI_REFERENCE_CONTENT_TYPE_BY_EXTENSION[extension] || ''
}

function createPreviewUrl(file) {
  return typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : ''
}

function revokePreviewUrl(url) {
  if (url && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url)
}

// Reference images for one AI image generation. The host owns the stored list
// of ready references ({ id, url, title, orgSlug }); each entry keeps the
// organization it was uploaded under and only the current organization's
// entries are exposed, so a reference is never sent to another organization's
// generation. Entries of other organizations stay stored (and reappear on
// switching back) instead of being cleared from an effect, which would race
// draft restore.
export function useReferenceImages({ orgSlug, value, onChange, uploadFile, deleteAsset }) {
  const [inFlight, setInFlight] = useState([])
  const previewUrlsRef = useRef(new Set())
  // One commit chain per organization: a stalled upload for one brand must
  // not hold back another brand's finished uploads.
  const commitChainsRef = useRef(new Map())

  useEffect(() => {
    const previewUrls = previewUrlsRef.current
    return () => {
      previewUrls.forEach(revokePreviewUrl)
      previewUrls.clear()
    }
  }, [])

  const ready = useMemo(
    () => (Array.isArray(value) ? value : []).filter((reference) => orgSlug && reference?.orgSlug === orgSlug),
    [orgSlug, value],
  )
  const uploadingForOrg = useMemo(
    () => inFlight.filter((item) => orgSlug && item.orgSlug === orgSlug),
    [inFlight, orgSlug],
  )

  const items = useMemo(() => [
    ...ready.map((reference) => ({
      key: reference.id,
      id: reference.id,
      url: reference.url,
      title: reference.title || '',
      status: 'ready',
    })),
    ...uploadingForOrg.map((item) => ({
      key: item.key,
      url: item.url,
      title: item.title,
      status: 'uploading',
    })),
  ], [ready, uploadingForOrg])

  const addFiles = useCallback((fileList) => {
    if (!orgSlug || !uploadFile) return
    const freeSlots = MAX_AI_REFERENCE_IMAGES - ready.length - uploadingForOrg.length
    const files = Array.from(fileList || []).slice(0, Math.max(0, freeSlots))
    files.forEach((file) => {
      const contentType = referenceContentType(file)
      if (!contentType) {
        toast.error('Reference images must be PNG, JPEG, or WebP.')
        return
      }
      if (file.size > MAX_AI_REFERENCE_BYTES) {
        toast.error('Image files must be 25 MB or smaller.')
        return
      }
      const key = crypto.randomUUID()
      const previewUrl = createPreviewUrl(file)
      if (previewUrl) previewUrlsRef.current.add(previewUrl)
      const uploadOrgSlug = orgSlug
      setInFlight((current) => [...current, { key, url: previewUrl, title: file.name, orgSlug: uploadOrgSlug }])
      // Uploads run in parallel and settle to { asset } or { error }, so a
      // rejection never goes unhandled while it waits for earlier files.
      const upload = Promise.resolve()
        .then(() => uploadFile(file, {
          title: file.name,
          asset_type: 'image',
          content_type: contentType,
          purpose: 'ai_reference',
        }))
        .then((asset) => ({ asset }), (error) => ({ error }))
      // Results are committed in selection order: the tile numbers are how the
      // prompt refers to each image, and the IDs are sent in that order. An
      // in-flight tile already sits at its final position, so committing it
      // moves it from the uploading tail to the end of the ready list in place.
      const previousCommit = commitChainsRef.current.get(uploadOrgSlug) || Promise.resolve()
      const commit = previousCommit
        .then(() => upload)
        .then(({ asset, error }) => {
          setInFlight((current) => current.filter((item) => item.key !== key))
          revokePreviewUrl(previewUrl)
          previewUrlsRef.current.delete(previewUrl)
          if (!error && asset?.id && asset?.url) {
            onChange((current) => [
              ...(Array.isArray(current) ? current : []),
              { id: asset.id, url: asset.url, title: asset.title || file.name, orgSlug: uploadOrgSlug },
            ])
            return
          }
          // Upload errors arrive as { error: { code, message } }; the helper also reads a plain string.
          toast.error(error
            ? imageGenerationErrorMessage(error, 'Failed to upload reference image')
            : 'Reference image upload returned no asset')
        })
        // Keep the chain alive for later files whatever happens above.
        .catch(() => {})
      commitChainsRef.current.set(uploadOrgSlug, commit)
      // Forget a settled chain so the map does not grow with every brand.
      commit.then(() => {
        if (commitChainsRef.current.get(uploadOrgSlug) === commit) commitChainsRef.current.delete(uploadOrgSlug)
      })
    })
  }, [onChange, orgSlug, ready.length, uploadFile, uploadingForOrg.length])

  const removeReference = useCallback((id) => {
    onChange((current) => (Array.isArray(current) ? current : []).filter((reference) => reference?.id !== id))
    if (!deleteAsset) return
    // Best effort: an orphaned hidden reference asset is harmless.
    Promise.resolve()
      .then(() => deleteAsset(id))
      .catch(() => {})
  }, [deleteAsset, onChange])

  return {
    items,
    addFiles,
    removeReference,
    uploading: uploadingForOrg.length > 0,
    canAdd: Boolean(orgSlug && uploadFile) && items.length < MAX_AI_REFERENCE_IMAGES,
  }
}
